// THE FREE CHECK'S ESTIMATOR ARITHMETIC — "WHAT COULD YOU SAVE?" — AS A MODULE WITH NO IMPORTS (R164; lifted out of
// `extras.ts` by R-pending-web-free-first, 2026-10-14).
//
// WHY IT IS ITS OWN FILE. The owner's own figures are computed on the device only and never sent to us (Tyler's decision
// 4, R164), so the arithmetic runs in every client: the iPhone's `FreeCheckEstimator.swift`, the marketing site's
// `free-check-extras.js`, and now app.proppeal.com's purchase page, which imports THIS file by alias
// (`@v2-freecheck-estimator`, web/vite.config.ts) rather than keeping a fourth copy. `extras.ts` carries the large
// registries (tax rates, the price index, the Census table); a client that imported the estimator from there would pull
// all of them into its bundle. So the arithmetic lives here, imports nothing, reads no clock and no environment, and
// `extras.ts` re-exports it unchanged — every reader of `extras.ts` sees the same functions it saw.
//
// THE REFERENCE THE CLIENTS ARE TESTED AGAINST: `functions/test/fixtures/freecheck/extras-vectors.json`, written by
// `functions/test/freecheckExtrasEndpoint.test.ts` from `estimatorOutcome` and `estimatorLines` below, and read by the
// app's `FreeCheckEstimatorTests` and the website's `web/test/freeFirst.test.tsx`. Registered as client-side debt at
// birth (`docs/THIN_CLIENT_DEBT.md`), with the reason.
//
// THE PA RATIO IS NEVER DISPLAYED. It moves the reduction and appears in no sentence this file fills (Tyler,
// 2026-09-28: "PA implied value: No").

/** The placeholders the client fills in the estimator's templates, and nothing else. */
export const EXTRAS_TEMPLATE_PARAMS = ["saving", "worth", "reduction", "count"] as const;

export interface EstimatorInput {
  /** The record's value, or the owner's notice value where there is none. */
  assessedValue: number;
  taxRate: number;
  ratio: number | null;
  /** What the owner says the home would sell for. */
  worth: number;
  /** `estimator.exemptionAmount`: the exemption on the county's record, or absent/null for none (EXTRAS-4). */
  exemptionAmount?: number | null;
}

export interface EstimatorOutcome {
  newAssessed: number;
  reduction: number;
  saving: number;
  /** `empty`: nothing to show yet (no figure, or no value to start from). */
  state: "saving" | "noSaving" | "empty";
}

/**
 * THE CONTRACT'S ARITHMETIC, EXACTLY: newAssessed = worth × (ratio ?? 1); reduction = max(0, assessed − newAssessed);
 * saving = round(min(reduction, taxable) × taxRate), where taxable = max(0, assessed − (exemptionAmount ?? 0)) — the one
 * floor EXTRAS-4 added: an exemption cannot make the taxable value negative, so the part of a reduction below it saves
 * nothing. With no exemption, taxable is the assessed value and the cap never binds (reduction ≤ assessed). `reduction`
 * stays the assessment's whole drop, which is what `{reduction}` says ("your assessment could drop by about …"). The
 * mockup's states: a saving above zero shows it; a figure that saves nothing says so; no figure shows nothing.
 */
export function estimatorOutcome(input: EstimatorInput): EstimatorOutcome {
  if (!(input.worth > 0) || !(input.assessedValue > 0) || !(input.taxRate > 0)) {
    return { newAssessed: 0, reduction: 0, saving: 0, state: "empty" };
  }
  const newAssessed = input.worth * (input.ratio ?? 1);
  const reduction = Math.max(0, input.assessedValue - newAssessed);
  const exempt = typeof input.exemptionAmount === "number" && input.exemptionAmount > 0 ? input.exemptionAmount : 0;
  const taxable = Math.max(0, input.assessedValue - exempt);
  const saving = Math.round(Math.min(reduction, taxable) * input.taxRate);
  return { newAssessed, reduction, saving, state: saving > 0 ? "saving" : "noSaving" };
}

/** US dollars, no cents: the client's format for `{saving}`, `{worth}` and `{reduction}`. */
export function formatDollars(n: number): string {
  return "$" + Math.round(n).toLocaleString("en-US");
}

/** The owner's typed figure, as the clients read it: whole dollars, anything from a decimal point on ignored. */
export function parseOwnerDollars(raw: string): number {
  const whole = String(raw ?? "").split(".")[0].replace(/[^0-9]/g, "");
  const n = whole === "" ? 0 : Number(whole);
  return Number.isFinite(n) ? n : 0;
}

/** A template with the client's values put in. Throws on a placeholder it was not given, as the server's own copy does. */
export function fillTemplate(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{([a-zA-Z]+)\}/g, (_, name: string) => {
    if (!(name in values)) throw new Error(`extras: the template needs {${name}}`);
    return values[name];
  });
}

/** The five templates the outcome's sentences are filled from: `estimator.templates` on the wire, or the vectors' own. */
export interface EstimatorLineTemplates {
  saving: string;
  explain: string;
  flagged: string;
  flaggedOne: string;
  noSaving: string;
}

/**
 * THE OUTCOME'S SENTENCES, IN ORDER: the server's templates with their `{placeholders}` filled (the vectors' `lines`
 * rule). In `saving`: the saving and the explanation always; `flaggedOne` for exactly one ticked box; `flagged` with
 * `{count}` the plain number for two or more; nothing for none. In `noSaving`: `noSaving`. In `empty`: none. The
 * disclaimer is not here: it is always shown with the estimator, whatever the figures. The client adds no words.
 */
export function estimatorLines(
  outcome: EstimatorOutcome, worth: number, flagged: number, templates: EstimatorLineTemplates,
): string[] {
  if (outcome.state === "noSaving") return [templates.noSaving];
  if (outcome.state !== "saving") return [];
  const lines = [
    fillTemplate(templates.saving, { saving: formatDollars(outcome.saving) }),
    fillTemplate(templates.explain, { worth: formatDollars(worth), reduction: formatDollars(outcome.reduction) }),
  ];
  if (flagged === 1) lines.push(templates.flaggedOne);
  if (flagged >= 2) lines.push(fillTemplate(templates.flagged, { count: String(flagged) }));
  return lines;
}
